import {
  applySnapshot,
  cast,
  flow,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import moment from 'moment';
import { isAbortError, withNetworkRetry } from 'utils/request';
import { Management } from '../../http-requests';

import { RequestStateModel } from './general-models';

const RequestsStatusesModel = types.model({
  createManagementReport: types.optional(RequestStateModel, {}),
  createExecutorsReport: types.optional(RequestStateModel, {}),
  createExecutorsContract: types.optional(RequestStateModel, {}),
  editExecutorContract: types.optional(RequestStateModel, {}),
  createContractsAgreement: types.optional(RequestStateModel, {}),
  getManagementReport: types.optional(RequestStateModel, {}),
  getExecutorsReport: types.optional(RequestStateModel, {}),
  getPreviousReport: types.optional(RequestStateModel, {}),
  getTariffChanges: types.optional(RequestStateModel, {}),
  getServices: types.optional(RequestStateModel, {}),
  getUnderServiceHouses: types.optional(RequestStateModel, {}),
  updateContractsAgreement: types.optional(RequestStateModel, {}),
  deleteContract: types.optional(RequestStateModel, {}),
  deleteContractsAgreement: types.optional(RequestStateModel, {}),
});

const MonthDataModel = types.model({
  month: types.optional(types.string, ''),
  total_accrued: types.optional(types.number, 0),
  total_paid: types.optional(types.number, 0),
  debt: types.optional(types.number, 0),
  accrued_resources: types.optional(types.number, 0),
  paid_for_resources: types.optional(types.number, 0),
  accrued_zkh: types.optional(types.number, 0),
  fact_funds: types.optional(types.number, 0),
  deficit_funds: types.optional(types.number, 0),
  tax: types.optional(types.number, 0),
  prediction: types.optional(types.boolean, false),
  z: types.optional(types.number, 100),
});

export interface IMonthDataModel extends Instance<typeof MonthDataModel> {}

const ParamsModel = types
  .model({
    house_id: types.optional(types.maybeNull(types.string), null),
    year: types.optional(types.string, String(moment().year() - 1)),
  })
  .actions((self) => ({
    setHouseId(id: string | null) {
      self.house_id = id;
    },
    setYear(year: string) {
      self.year = year;
    },
  }));

export interface IParamsManagementModel extends Instance<typeof ParamsModel> {}

const ManagementReportModel = types.model({
  data: types.optional(types.array(MonthDataModel), []),
  including_tax: types.optional(types.array(MonthDataModel), []),
  by_resources_on_direct_contracts: types.optional(
    types.array(MonthDataModel),
    []
  ),
  by_resources_on_direct_contracts_including_tax: types.optional(
    types.array(MonthDataModel),
    []
  ),
});

export interface IManagementReportModel extends Instance<
  typeof ManagementReportModel
> {}

const ServiceTypeMST = types.enumeration('ServiceType', [
  'fact_funds',
  'communal',
]);

const AreaChangesModel = types.model({
  service_name: types.optional(types.string, ''),
  from: types.optional(types.number, 0),
  to: types.optional(types.number, 0),
  service_type: types.optional(ServiceTypeMST, 'fact_funds'),
});

export interface IAreaChangesModel extends Instance<typeof AreaChangesModel> {}

const ChangesModel = types
  .model({
    LivingArea: types.optional(types.array(AreaChangesModel), []), //Нет возможности переименовать под camelCase
    NotLivingArea: types.optional(types.array(AreaChangesModel), []), // из за того что такой ответ приходит с бэка
  })
  .views((self) => ({
    get changesCount() {
      return self.LivingArea.length + self.NotLivingArea.length;
    },
    get byResourcesCount() {
      return (
        self.LivingArea.filter((item) => item.service_type !== 'communal')
          .length +
        self.NotLivingArea.filter((item) => item.service_type !== 'communal')
          .length
      );
    },
  }));

const TariffChangesModel = types.model({
  month: types.optional(types.string, ''),
  changes: types.optional(ChangesModel, {}),
});

const PerformerMonthModel = types
  .model({
    income: types.optional(types.maybeNull(types.number), 0),
    outcome: types.optional(types.maybeNull(types.number), 0),
  })
  .views((self) => ({
    get stonks(): boolean {
      return (self.income || 0) > (self.outcome || 0);
    },
    get isSame(): boolean {
      return (self.income || 0) === (self.outcome || 0);
    },
  }));

export interface IPerformerMonthModel extends Instance<
  typeof PerformerMonthModel
> {}

const PerformerContractsHousesModel = types.model({
  id: types.optional(types.string, ''),
  address: types.optional(types.string, ''),
  months: types.optional(types.array(PerformerMonthModel), []),
});

export interface IPerformerContractsHousesModel extends Instance<
  typeof PerformerContractsHousesModel
> {}

const ContractStatusTypeMST = types.enumeration('ContractStatusType', [
  'active',
  'cancelled',
]);

type ContractStatusType = 'active' | 'cancelled';

const PerformerContractsDataModel = types
  .model({
    id: types.optional(types.string, ''),
    number: types.optional(types.string, ''),
    status: types.optional(ContractStatusTypeMST, 'active'),
    conclusion_date: types.optional(types.maybeNull(types.string), ''),
    termination_date: types.optional(types.maybeNull(types.string), ''),
    months: types.optional(types.array(PerformerMonthModel), []),
    houses: types.optional(types.array(PerformerContractsHousesModel), []),
    isCollapsed: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    setCollapsed(val: boolean) {
      self.isCollapsed = val;
    },
  }));

export interface IPerformerContractsDataModel extends Instance<
  typeof PerformerContractsDataModel
> {}

const PerformerDataModel = types
  .model({
    id: types.optional(types.string, ''),
    type: types.optional(types.maybeNull(types.string), null),
    name: types.optional(types.maybeNull(types.string), ''),
    inn: types.optional(types.string, ''),
    kpps: types.optional(types.array(types.string), []),
    bank_numbers: types.optional(types.array(types.string), []),
    months: types.optional(types.array(PerformerMonthModel), []),
    contracts: types.optional(types.array(PerformerContractsDataModel), []),
    isCollapsed: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get allClosed() {
      return (
        !!self.contracts.length &&
        self.contracts.every((item) => item.status === 'cancelled')
      );
    },
  }))
  .actions((self) => ({
    setCollapsed(val: boolean) {
      self.isCollapsed = val;
    },
  }));

export interface IPerformerDataModel extends Instance<
  typeof PerformerDataModel
> {}

const AgreementsModel = types.model({
  id: types.optional(types.maybeNull(types.string), null),
  service_id: types.optional(types.maybeNull(types.string), null),
  house_ids: types.optional(types.array(types.string), []),
  settlement_range: types.optional(types.maybeNull(types.string), null),
  is_post_payment: types.optional(types.boolean, false),
  settlement_type: types.optional(types.maybeNull(types.string), null),
  amount: types.optional(types.maybeNull(types.number), 0),
});

export interface IAgreementsModel extends Instance<typeof AgreementsModel> {}

const ContractParamsModel = types
  .model({
    id: types.optional(types.string, ''),
    type: types.optional(types.maybeNull(types.string), null),
    number: types.optional(types.string, ''),
    conclusion_date: types.optional(types.maybeNull(types.string), null),
    status: types.optional(ContractStatusTypeMST, 'active'), //cancelled
    duration_in_months: types.optional(types.maybeNull(types.string), null),
    extension_type: types.optional(types.maybeNull(types.string), null),
    termination_type: types.optional(types.maybeNull(types.string), null),
    extension_period_in_months: types.optional(
      types.maybeNull(types.string),
      null
    ),
    termination_date: types.optional(types.maybeNull(types.string), null),
    agreements: types.optional(types.array(AgreementsModel), []),
  })
  .actions((self) => ({
    setType(type: string | null) {
      self.type = type;
    },
    setNumber(number: string) {
      self.number = number;
    },
    setConclusionDate(date: string | null) {
      self.conclusion_date = date;
    },
    setStatus(status: ContractStatusType) {
      self.status = status;
    },
    setDuration(duration: string) {
      self.duration_in_months = duration;
    },
    setExtensionType(extension: string | null) {
      self.extension_type = extension;
    },
    setTerminationType(terminationType: string | null) {
      self.termination_type = terminationType;
    },
    setExtensionPeriod(period: string | null) {
      self.extension_period_in_months = period;
    },
    setTerminationDate(date: string | null) {
      self.termination_date = date;
    },
    addAgreements(agreement: IAgreementsModel) {
      //TODO: value `undefined` is not assignable to type: `AnonymousModel`
      self.agreements.push(agreement);
    },
    changeAgreementLocal(agreement: IAgreementsModel) {
      self.agreements = cast(
        self.agreements.map((item) => {
          return item.id === agreement.id ? agreement : item;
        })
      );
    },
    deleteAgreements(id: string) {
      self.agreements = cast(self.agreements.filter((item) => item.id !== id));
    },
  }));

export interface IContractParamsModel extends Instance<
  typeof ContractParamsModel
> {}

const ContractPageTypeMST = types.enumeration('ContractPageType', [
  'new',
  'edit',
]);

type ContractPageType = 'new' | 'edit';

const SelectedExecutorModel = types
  .model({
    id: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    bank_numbers: types.optional(types.array(types.string), []),
    inn: types.optional(types.maybeNull(types.string), null),
    kpps: types.optional(types.array(types.string), []),
    contractPageType: types.optional(ContractPageTypeMST, 'new'),
  })
  .actions((self) => ({
    setSelectExecutor(performer: IPerformerDataModel) {
      applySnapshot(self.bank_numbers, performer.bank_numbers);
      applySnapshot(self.kpps, performer.kpps);
      self.name = performer?.name || 'Без названия';
      self.inn = performer.inn;
      self.id = performer.id;
    },
    setContractPageType(type: ContractPageType) {
      self.contractPageType = type;
    },
  }));

const ExecutorsModel = types.model({
  no_contracts_executors: types.optional(types.array(PerformerDataModel), []),
  service_executors: types.optional(types.array(PerformerDataModel), []),
  resource_executors: types.optional(types.array(PerformerDataModel), []),
});

const ServicesModel = types.model({
  id: types.optional(types.string, ''),
  title: types.optional(types.string, ''),
  code: types.optional(types.maybeNull(types.string), null), //TODO: нет поля в новом запросе
});

const TabIndicatorState = types.enumeration('TabIndicatorState', [
  'idle',
  'pending',
  'error',
  'success',
  'seen',
]);

const TabIndicatorsModel = types.model({
  main: types.optional(TabIndicatorState, 'idle'),
  executor: types.optional(TabIndicatorState, 'idle'),
});

const TabTypeMST = types.enumeration('TabType', [
  'main',
  'executor',
  'accrual',
  'contract',
  'view_contract',
]);

export type IManagementTab =
  | 'main'
  | 'executor'
  | 'accrual'
  | 'contract'
  | 'view_contract';

const ServicesDataModel = types.model({
  resource_services: types.optional(types.array(ServicesModel), []),
  non_resource_services: types.optional(types.array(ServicesModel), []),
});

export const ManagementAccountingStore = types
  .model({
    monthData: types.optional(ManagementReportModel, {}),
    selectedData: types.optional(MonthDataModel, {}),
    params: types.optional(ParamsModel, {}),
    lastParams: types.optional(ParamsModel, {}),
    selectedTab: types.optional(TabTypeMST, 'main'),
    ndsChecked: types.optional(types.boolean, false),
    requestsStatuses: types.optional(RequestsStatusesModel, {}),
    saveReport: types.optional(types.boolean, false),
    tariffChanges: types.optional(types.array(TariffChangesModel), []),
    executors: types.optional(ExecutorsModel, {}),
    contractParams: types.optional(ContractParamsModel, {}),
    newContractParams: types.optional(ContractParamsModel, {}),
    selectedExecutor: types.optional(SelectedExecutorModel, {}),
    services: types.optional(ServicesDataModel, {}),
    newAgreement: types.optional(AgreementsModel, {}),
    changedMade: types.optional(types.boolean, false),
    showInformer: types.optional(types.boolean, false),
    mainReportId: types.optional(types.maybeNull(types.string), null),
    executorReportId: types.optional(types.maybeNull(types.string), null),
    mainProgress: types.optional(types.number, 0), // прогресс основного отчёта, %
    executorsProgress: types.optional(types.number, 0), // прогресс отчёта по исполнителям, %
    mainAttempt: types.optional(types.number, 1), // номер попытки запроса основного отчёта (1–3)
    executorsAttempt: types.optional(types.number, 1), // номер попытки запроса отчёта по исполнителям (1–3)
    tabIndicators: types.optional(TabIndicatorsModel, {}), // состояние иконок на вкладках: idle/pending/error/success/seen
    activeReport: types.optional(
      types.enumeration(['main', 'executor', 'none']),
      'none'
    ), // какой отчёт сейчас строится — от него зависит, где показывать %
  })
  .volatile(() => ({
    abortController: null as AbortController | null,
  }))
  .views((self) => ({
    get disableCreate(): boolean {
      if (self.changedMade) {
        return false;
      }

      // 1. Валидация: «По МКД» без выбранного адреса
      if (
        typeof self.params.house_id === 'string' &&
        !self.params.house_id.length
      ) {
        return true;
      }

      // 2. Параметры совпадают с последними успешными И отчёт уже построен
      const sameParams =
        self.params.house_id === self.lastParams.house_id &&
        self.params.year === self.lastParams.year;

      if (sameParams) {
        if (
          self.selectedTab === 'main' &&
          self.requestsStatuses.getManagementReport.fulfilled
        ) {
          return true;
        }

        if (
          self.selectedTab === 'executor' &&
          self.requestsStatuses.getExecutorsReport.fulfilled
        ) {
          return true;
        }
      }

      return false;
    },
  }))
  .actions((self) => ({
    setTabIndicator(
      tab: 'main' | 'executor',
      value: 'idle' | 'pending' | 'error' | 'success' | 'seen'
    ) {
      if (
        (value === 'success' || value === 'error') &&
        self.selectedTab === tab
      ) {
        self.tabIndicators[tab] = 'seen';

        return;
      }

      self.tabIndicators[tab] = value;
    },
    markTabSeen(tab: 'main' | 'executor') {
      self.tabIndicators[tab] = 'seen';
    },
    setActiveReport(report: 'main' | 'executor' | 'none') {
      self.activeReport = report;
    },
  }))
  .actions((self) => ({
    setMainProgress(value: number) {
      self.mainProgress = value;
    },
    setExecutorsProgress(value: number) {
      self.executorsProgress = value;
    },
    setMainAttempt(value: number) {
      self.mainAttempt = value;
    },
    setExecutorsAttempt(value: number) {
      self.executorsAttempt = value;
    },
    failExecutorsReport() {
      if (self.requestsStatuses.getExecutorsReport.error) {
        return;
      }

      self.executorReportId = null;
      self.executorsProgress = 0;
      self.executorsAttempt = 1;
      self.setTabIndicator('executor', 'error');
      self.setActiveReport('none');
      self.requestsStatuses.getExecutorsReport.errorStates();
    },
    failMainReport() {
      if (self.requestsStatuses.getManagementReport.error) {
        return;
      }

      self.mainReportId = null;
      self.mainProgress = 0;
      self.mainAttempt = 1;
      self.setTabIndicator('main', 'error');
      self.setActiveReport('none');
      self.requestsStatuses.getManagementReport.errorStates();
    },
    abortCurrentRequests() {
      if (self.abortController) {
        self.abortController.abort();
        self.abortController = null;
      }
    },
  }))
  .actions((self) => ({
    createExecutorsReport: flow(function* () {
      try {
        // Абортим предыдущие запросы
        self.abortCurrentRequests();
        self.abortController = new AbortController();
        const signal = self.abortController.signal;

        self.setTabIndicator('executor', 'pending');
        self.setActiveReport('executor');

        self.requestsStatuses.createExecutorsReport.loadingStates();
        self.requestsStatuses.getExecutorsReport.clearStates();

        self.executorsProgress = 0;
        self.executorsAttempt = 1;
        applySnapshot(self.executors, {});

        const response = yield Management.CreateExecutorsReport(
          self.params,
          signal
        );

        // защита от гонки
        if (signal.aborted) {
          return;
        }

        if (
          JSON.stringify(getSnapshot(self.params)) !==
          JSON.stringify(getSnapshot(self.lastParams))
        ) {
          applySnapshot(self.lastParams, getSnapshot(self.params));
        }

        self.executorReportId = response.task_id;
        self.requestsStatuses.createExecutorsReport.successStates();

        return response;
      } catch (e) {
        //TODO: SyntaxError: Unexpected token S in JSON at position 0 (Service is unavailable в ответе на запрос)
        // при статусе 200

        if (isAbortError(e)) {
          return;
        }

        self.setTabIndicator('executor', 'error');
        self.setActiveReport('none');
        self.executorsProgress = 0;
        self.executorsAttempt = 1;
        self.requestsStatuses.createExecutorsReport.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.createExecutorsReport.finallyStates();
      }
    }),
  }))
  .actions((self) => ({
    clearServices() {
      applySnapshot(self.services, {
        resource_services: [],
        non_resource_services: [],
      });
    },
    clearAll() {
      const savedIndicators = getSnapshot(self.tabIndicators);

      applySnapshot(self, {});
      applySnapshot(self.tabIndicators, savedIndicators);
    },
    clearSelectedData() {
      applySnapshot(self.selectedData, {});
    },
    clearExecutorsData() {
      applySnapshot(self.executors, {});
    },
    clearSaveReport() {
      self.saveReport = false;
    },
    clearSelectedExecutor() {
      applySnapshot(self.selectedExecutor, {});
    },
    clearContractParams() {
      applySnapshot(self.contractParams, {});
    },
    clearNewContractParams() {
      applySnapshot(self.newContractParams, {});
    },
    setChangedMade(bool: boolean) {
      self.changedMade = bool;
    },
    setShowInformer(bool: boolean) {
      self.showInformer = bool;
    },
    setParams(params: IParamsManagementModel) {
      self.params = params;
    },
    setSelectedTab(tab: IManagementTab) {
      self.selectedTab = tab;

      if (tab === 'main' || tab === 'executor') {
        self.markTabSeen(tab);
      }
    },
    setSelectedData(value: IMonthDataModel) {
      self.selectedData = value;
    },
    setNdsChecked(bool: boolean) {
      self.ndsChecked = bool;
    },
    setContractParams(contractParams: IContractParamsModel) {
      self.contractParams = contractParams;
    },
    createManagementReport: flow(function* () {
      try {
        // Абортим предыдущие запросы
        self.abortCurrentRequests();
        self.abortController = new AbortController();
        const signal = self.abortController.signal;

        self.setTabIndicator('main', 'pending');
        self.setTabIndicator('executor', 'pending');
        self.setActiveReport('main');

        self.requestsStatuses.createManagementReport.loadingStates();
        self.requestsStatuses.getManagementReport.clearStates();

        self.mainProgress = 0;
        self.mainAttempt = 1;
        applySnapshot(self.monthData, {});
        applySnapshot(self.tariffChanges, []);

        const response = yield Management.CreateManagementAccountingReport(
          self.params,
          signal
        );

        // защита от гонки
        if (signal.aborted) {
          return;
        }

        if (
          JSON.stringify(getSnapshot(self.params)) !==
          JSON.stringify(getSnapshot(self.lastParams))
        ) {
          applySnapshot(self.lastParams, getSnapshot(self.params));
        }

        self.mainReportId = response.task_id;
        self.requestsStatuses.createManagementReport.successStates();

        return response;
      } catch (e) {
        if (isAbortError(e)) {
          return;
        }

        self.setTabIndicator('main', 'error');
        self.setActiveReport('none');
        self.mainProgress = 0;
        self.mainAttempt = 1;
        self.requestsStatuses.createManagementReport.errorStates();
        console.error(e);

        self.createExecutorsReport();
      } finally {
        self.requestsStatuses.createManagementReport.finallyStates();
      }
    }),
    createTariffChanges: flow(function* () {
      try {
        self.requestsStatuses.getTariffChanges.loadingStates();
        applySnapshot(self.tariffChanges, []);

        const response = yield Management.CreateTariffChanges({
          ...self.params,
        });

        self.tariffChanges = cast(response.result);
        self.requestsStatuses.getTariffChanges.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.getTariffChanges.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.getTariffChanges.finallyStates();
      }
    }),
    createExecutorContract: flow(function* () {
      try {
        self.requestsStatuses.createExecutorsContract.loadingStates();

        const response = yield Management.CreateExecutorContract({
          ...self.newContractParams,
          executor_id: self.selectedExecutor.id,
        });

        // В ответе два поля
        //info (bank_numbers, inn, id, kpps, name)
        //params (agreements, conclusion_date, duration_in_month, ... и т.д.)
        self.contractParams = response.params;

        self.requestsStatuses.createExecutorsContract.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.createExecutorsContract.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.createExecutorsContract.finallyStates();
      }
    }),
    editExecutorContract: flow(function* () {
      try {
        self.requestsStatuses.editExecutorContract.loadingStates();

        const response = yield Management.EditExecutorsContract(
          self.contractParams.id,
          self.contractParams
        );
        self.contractParams = response.params; //Записывается туда же, куда и ответ createExecutorContract

        self.requestsStatuses.editExecutorContract.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.editExecutorContract.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.editExecutorContract.finallyStates();
      }
    }),
    createContractsAgreement: flow(function* (
      agreements: IAgreementsModel,
      contractId: string
    ) {
      try {
        self.requestsStatuses.createContractsAgreement.loadingStates();

        const response = yield Management.CreateContractsAgreement({
          ...agreements,
          contract_id: contractId,
        });

        self.requestsStatuses.createContractsAgreement.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.createContractsAgreement.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.createContractsAgreement.finallyStates();
      }
    }),
    getManagementReport: flow(function* () {
      try {
        self.requestsStatuses.getManagementReport.loadingStates();
        applySnapshot(self.monthData, {});

        const response = yield withNetworkRetry(
          () =>
            Management.GetManagementAccountingReportData(
              self.mainReportId || '',
              { save: self.saveReport },
              self.abortController?.signal
            ),
          self.abortController?.signal,
          (attempt) => self.setMainAttempt(attempt)
        );

        if (self.abortController?.signal.aborted) {
          return;
        }

        self.monthData = response.result;
        self.mainReportId = null;

        self.setTabIndicator('main', 'success');
        self.setActiveReport('none');
        self.requestsStatuses.getManagementReport.successStates();

        return response;
      } catch (e) {
        if (isAbortError(e)) {
          return;
        }

        self.mainReportId = null;
        self.setTabIndicator('main', 'error');
        self.setActiveReport('none');
        self.mainProgress = 0;
        self.mainAttempt = 1;
        self.requestsStatuses.getManagementReport.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.getManagementReport.finallyStates();
      }
    }),
    getExecutorsReport: flow(function* () {
      try {
        self.requestsStatuses.getExecutorsReport.loadingStates();

        const response = yield withNetworkRetry(
          () =>
            Management.GetExecutorsReportData(
              self.executorReportId || '',
              self.abortController?.signal
            ),
          self.abortController?.signal,
          (attempt) => self.setExecutorsAttempt(attempt)
        );

        if (self.abortController?.signal.aborted) {
          return;
        }

        self.executors = response.result;
        self.executorReportId = null;

        self.setTabIndicator('executor', 'success');
        self.setActiveReport('none');
        self.requestsStatuses.getExecutorsReport.successStates();

        return response;
      } catch (e) {
        if (isAbortError(e)) {
          return;
        }

        self.executorReportId = null;
        self.setTabIndicator('executor', 'error');
        self.setActiveReport('none');
        self.executorsProgress = 0;
        self.executorsAttempt = 1;
        self.requestsStatuses.getExecutorsReport.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.getExecutorsReport.finallyStates();
      }
    }),
    getPreviousYearReport: flow(function* () {
      try {
        self.requestsStatuses.getPreviousReport.loadingStates();

        const response = yield Management.GetPreviousYearReportData();

        if (response.exists) {
          self.monthData = response.result;
        } else {
          self.saveReport = true;
        }

        self.requestsStatuses.getPreviousReport.successStates();

        return response.exists;
      } catch (e) {
        self.requestsStatuses.getPreviousReport.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.getPreviousReport.finallyStates();
      }
    }),
    getExecutorContractData: flow(function* (id: string) {
      try {
        self.requestsStatuses.getManagementReport.loadingStates();
        applySnapshot(self.monthData, {});

        const response = yield Management.GetExecutorContractData(id);
        self.contractParams = response.params;
        self.selectedExecutor = response.info;
        self.requestsStatuses.getManagementReport.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.getManagementReport.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.getManagementReport.finallyStates();
      }
    }),
    getServices: flow(function* (params?: {
      start_period: string | null;
      end_period: string | null;
    }) {
      try {
        self.requestsStatuses.getServices.loadingStates();
        const response = yield Management.GetProviderAccrualServices(params);
        self.services = response;
        self.requestsStatuses.getServices.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.getServices.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.getServices.finallyStates();
      }
    }),
    getUnderServiceHouses: flow(function* (
      conclusionDate: string | number,
      terminationDate: string | number | null,
      id: string
    ) {
      try {
        self.requestsStatuses.getUnderServiceHouses.loadingStates();
        const response = yield Management.GetUnderServiceHouses({
          start_year: conclusionDate,
          service_id: id,
          end_year: terminationDate || moment().year(),
        });
        self.requestsStatuses.getUnderServiceHouses.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.getUnderServiceHouses.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.getUnderServiceHouses.finallyStates();
      }
    }),
    updateContractsAgreement: flow(function* (agreement: IAgreementsModel) {
      try {
        self.requestsStatuses.updateContractsAgreement.loadingStates();

        const { id, ...data } = agreement;
        const response = yield Management.UpdateContractsAgreement(
          id || '',
          data
        );

        self.requestsStatuses.updateContractsAgreement.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.updateContractsAgreement.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.updateContractsAgreement.finallyStates();
      }
    }),
    deleteContract: flow(function* (id: string) {
      try {
        self.requestsStatuses.deleteContract.loadingStates();
        const response = yield Management.DeleteExecutorContract(id);
        self.requestsStatuses.deleteContract.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.deleteContract.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.deleteContract.finallyStates();
      }
    }),
    deleteContractsAgreement: flow(function* (id: string) {
      try {
        self.requestsStatuses.deleteContractsAgreement.loadingStates();
        const response = yield Management.DeleteContractsAgreement(id);
        self.requestsStatuses.deleteContractsAgreement.successStates();

        return response;
      } catch (e) {
        self.requestsStatuses.deleteContractsAgreement.errorStates();
        console.error(e);
      } finally {
        self.requestsStatuses.deleteContractsAgreement.finallyStates();
      }
    }),
  }));
